// Use a relative path so `apiCall()` prepends `/ECMv6/api` and the dev proxy can forward it.
// This avoids browser CORS issues in local development.
import React from 'react';
import { Icon } from '@saviynt/design-system';

const getApiBaseUrl = () => '/legacy-migration';

export const API_BASE_URL = getApiBaseUrl();

export const APPLICATION_TYPE_MAPPING = {
  snow: 'ServiceNow',
  'microsoft active directory': 'Active Directory - Direct',
  'microsoft ad': 'Active Directory - Direct',
  'active directory': 'Active Directory - Direct',
  'synthetic app': 'JDBC',
};

export const OBJECT_TYPE_ICONS = {
  Application: <Icon kind="Applications" label="Applications" size="xxLarge" />,
  PasswordPolicy: <Icon kind="Policy" label="Policy" size="xxLarge" />,
  EmailTemplate: <Icon kind="Mail" label="Mail" size="xxLarge" />,
  Workflow: <Icon kind="Workflow" label="Workflow" size="xxLarge" />,
  Bundle: <Icon kind="EnterpriseRole" label="EnterpriseRole" size="xxLarge" />,
  TaskDefinition: <Icon kind="Task" label="Task" size="xxLarge" />,
  SPRight: <Icon kind="Boolean" label="Boolean" size="xxLarge" />,
  Form: <Icon kind="DynamicAttributes" label="DynamicAttributes" size="xxLarge" />,
  Rule: <Icon kind="Function" label="Function" size="xxLarge" />,
  Identity: <Icon kind="User" label="User" size="xxLarge" />,
  Account: <Icon kind="Account" label="Account" size="xxLarge" />,
  Entitlement: <Icon kind="Entitlement" label="Entitlement" size="xxLarge" />,
  Applications: <Icon kind="Applications" label="Applications" size="xxLarge" />,
  CorrelationConfig: <Icon kind="Link" label="Link" size="xxLarge" />,
  CertificationDefinition: <Icon kind="Certification" label="Certification" size="xxLarge" />,
  ObjectConfig: <Icon kind="Object" label="Object" size="xxLarge" />,
  Configuration: <Icon kind="Settings" label="Settings" size="xxLarge" />,
  UIConfig: <Icon kind="WebAppConsole" label="WebAppConsole" size="xxLarge" />,
  YAMLConfig: <Icon kind="Code" label="Code" size="xxLarge" />,
  Policy: <Icon kind="Policy" label="Policy" size="xxLarge" />,
  Capability: <Icon kind="Feature" label="Feature" size="xxLarge" />,
  IdentityTrigger: <Icon kind="JobFunction" label="JobFunction" size="xxLarge" />,
  DynamicScope: <Icon kind="Preview" label="Preview" size="xxLarge" />,
  QuickLink: <Icon kind="JumpLink" label="JumpLink" size="xxLarge" />,
  Dictionary: <Icon kind="List" label="List" size="xxLarge" />,
  Custom: <Icon kind="DeveloperTool" label="DeveloperTool" size="xxLarge" />,
  Workgroup: <Icon kind="Group" label="Group" size="xxLarge" />,
  GroupDefinition: <Icon kind="Group" label="Group" size="xxLarge" />,
  DatabaseVersion: <Icon kind="Database" label="Database" size="xxLarge" />,
  // OIM object types
  RequestableEntitlements: <Icon kind="RequestAccess" label="RequestAccess" size="xxLarge" />,
  UsersAssignedEntitlements: <Icon kind="BurnKey" label="BurnKey" size="xxLarge" />,
  Datasets: <Icon kind="DataTable" label="DataTable" size="xxLarge" />,
  EnterpriseRoles: <Icon kind="EnterpriseRole" label="EnterpriseRole" size="xxLarge" />,
  UserGroups: <Icon kind="Group" label="Group" size="xxLarge" />,
  EmailTemplates: <Icon kind="Mail" label="Mail" size="xxLarge" />,
  RoleMemberships: <Icon kind="EnterpriseRole" label="EnterpriseRole" size="xxLarge" />,
};

export const SOURCE_CONFIGS = {
  sailpoint: {
    name: 'SailPoint',
    logo: '/ECMv6/assets/images/SP_logo.png',
    url: null, // Will be fetched dynamically
    description:
      'Migrate applications, workflows, and configurations from SailPoint IdentityIQ to Saviynt',
  },
  oim: {
    name: 'Oracle Identity Manager',
    logo: '/ECMv6/assets/images/Oracle.png',
    url: null, // Will be fetched dynamically
    description:
      'Migrate connectors, workflows, and configurations from Oracle Identity Manager to Saviynt',
  },
};

export const MIGRATION_STEPS = [
  {
    icon: '🤝',
    title: 'Discover objects',
    description: 'Connect to your environment and get visibility to different objects present.',
    step: 1,
  },
  {
    icon: '📄',
    title: 'Migrate Configuration',
    description: 'Choose and migrate various configuration objects & Applications.',
    step: 2,
  },
  {
    icon: '📋',
    title: 'Migrate Data',
    description: 'Build Identity Warehouse by migrating data objects.',
    step: 3,
  },
];

export const getApplicationType = (objectName) => {
  const name = objectName.toLowerCase();

  const entry = Object.entries(APPLICATION_TYPE_MAPPING).find(([keyword]) =>
    name.includes(keyword)
  );
  return entry ? entry[1] : null;
};

export const getObjectIcon = (objectType) =>
  OBJECT_TYPE_ICONS[objectType] || <Icon kind="AddCircle" label="AddCircle" size="xxLarge" />;

export const JOB_STATUS = {
  CREATED: 'CREATED',
  RUNNING: 'RUNNING',
  FAILED: 'FAILED',
  COMPLETED: 'COMPLETED',
};

export const MIGRATION_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

export const SAILPOINT_TOOLTIP_DESCRIPTIONS = {
  APPLICATION: `IdentityIQ uses configured applications to connect to target systems and read data from those applications.<br/><b>Saviynt equivalent:</b> Connection, Security System, Endpoint`,
  BUNDLE: `Business Role identify job functions or titles, or other attributes by which users can be grouped together using a criteria. Also, Encapsulate sets of system entitlements from one or more applications to be grouped together into a single role \n<b>Saviynt equivalent:</b> Policies/Roles`,
  CAPABILITY: `Capabilities group one or more access rights and Rights tend to be limited and granular, and are often used in combination to provide full access to a feature or area of the UI.<br/><b>Saviynt equivalent:</b> Sav Role.`,
  CERTIFICATION_DEFINITION: `Certification Definition is a persistent configuration object that serves as the "blueprint" or "template" for a certification campaign.<br/><b>Saviynt equivalent:</b> Certifications.`,
  CONFIGURATION: `Configuration object is a global, XML-based system object that acts as the primary "settings" file for the entire environment.<br/><b>Saviynt equivalent:</b> Global Configuration.`,
  CORRELATION_CONFIG: `Correlation refers to how IdentityIQ maps data from the source system to authoritative identities.<br/><b>Saviynt equivalent:</b> User Account Correlation rule.`,
  DATABASE_VERSION: `Shows product and patch version.<br/><b>Saviynt equivalent:</b> NA.`,
  DICTIONARY: `set of words (or character strings) that have been deemed impermissible as passwords or password contents.`,
  DYNAMIC_SCOPE: `Quick link populations grant access to specific areas of IdentityIQ to predetermined populations of users.<br/><b>Saviynt equivalent:</b> Sav Role (Who can request the access for whom, Create Request Home Option, Dashboards)`,
  EMAIL_TEMPLATE: `Many events in IdentityIQ generate email notifications to notify users of actions required by them or actions taken that directly affects them. These email messages are created based on email templates.<br/><b>Saviynt equivalent:</b> Email Template`,
  FORM: `Forms are used to present items to users for input in several components of IdentityIQ. Provisioning policies in an application configuration define the set of attributes which are needed to complete a provisioning request, whether that request is to create an account, modify an account, add a role to an identity, etc.<br/><b>Saviynt equivalent:</b> Connection (account JSONs) + Dynamic Attribute (additional user inputs)`,
  GROUP_DEFINITION: `Contain the actual filter used to match identities to the group. Populations are also stored as Group Definition objects.<br/><b>Saviynt equivalent:</b> Analytics`,
  IDENTITY_TRIGGER: `Lifecycle events can be configured to run based on events that occur in IdentityIQ.<br/><b>Saviynt equivalent:</b> Technical/User Update Rules`,
  PASSWORD_POLICY: `Password Policy can defined at  application or global level. You can further define criteria to have separate policy for different group of users. For instance, employee might follow 8 characters length password but for service account it can be 16 characters.<br/><b>Saviynt equivalent:</b> Password Policy`,
  POLICY: `Policies in IdentityIQ check identities for certain conditions that are unwanted, or even considered dangerous.<br/><b>Saviynt equivalent:</b> Separation of Duties (SOD)`,
  QUICK_LINK: `Quick links are objects in IdentityIQ that enable you to place customized links on the IdentityIQ.<br/><b>Saviynt equivalent:</b> Sav Roles`,
  RULE: `Rules in IdentityIQ allow the addition of custom business logic at specific points within the execution flow of the product. Rules are written in Bean Shell scripting language.<br/><b>Saviynt equivalent:</b> Groovy scripts/SQL queries/Pre processor`,
  SP_RIGHT: `IdentityIQ uses a security model based on rights that are granted to users, to control access to product features. These rights (also called SPRights) turn on or off menus, tools, pages, and tabs in the UI, and thus can limit the actions users can take within IdentityIQ. \n<b>Saviynt equivalent:</b> (Feature Access, Web Service Access, Analytics in Sav Roles) `,
  TASK_DEFINITION: `Task are used to automate the processes which build, update, and maintain the information/data.<br/><b>Saviynt equivalent:</b> Jobs`,
  UI_CONFIG: `Configuration object is a global, XML-based system object that acts as the primary "settings" file for UI.<br/><b>Saviynt equivalent:</b> Global Configuration`,
  WORKFLOW: `Defines the workflow structure and steps involved in the workflow processing.<br/><b>Saviynt equivalent:</b> Workflow`,
  YAML_CONFIG: `YAML Config typically refers to a modern approach for managing object configurations and moving away from XML.<br/><b>Saviynt equivalent:</b> Global Configuration`,
  ACCOUNT: `Represents count of accounts on the target system.`,
  ENTITLEMENT: `Represents count of access/permission on the target systems.`,
  IDENTITY: `Represents counts of a Person, Machine Account, or AI Agent who has access within the governed environment.`,
  OBJECT_CONFIG: `ObjectConfig is a core configuration object used to define and extend the metadata for primary system entities.`,
  CUSTOM: `Special type of configuration object that allows you to store arbitrary/static data or logic (key-value pairs, configuration, lookup tables, mappings).<br/><b>Saviynt equivalent:</b> Dataset.`,
  WORKGROUP: `A Workgroup is a grouping of Identities that can be assigned activities within IdentityIQ as if the group were a single Identity. Workgroups are primarily used in two ways: for allowing Identities to share responsibilities, and for managing IdentityIQ Access for groups of Identities as a unit..<br/><b>Saviynt equivalent:</b> User Groups.`,
  DATASETS: `Static key-value pairs used to populate form drop-downs and translate data during reconciliation.<br/><b>Saviynt equivalent:</b> Datasets (Key-value lists used for form values and mapping logic).`,
  ENTERPRISE_ROLES: `Logical containers for RBAC that group users to apply Access Policies automatically.<br/><b>Saviynt equivalent:</b> Roles (Enterprise or Business Roles used to bundle entitlements for assignment).`,
  REQUESTABLE_ENTITLEMENTS: `Specific permissions or groups exposed in the service catalog for ad-hoc user requests.<br/><b>Saviynt equivalent:</b> Entitlements (specifically those marked "Requestable" in the Entitlement Warehouse).`,
  USER_GROUPS: `Groups that define administrative privileges and visibility within the OIM console itself.<br/><b>Saviynt equivalent:</b> SAV Roles.`,
  USERS_ASSIGNED_ENTITLEMENTS: `The specific instance of a resource or child-table permission currently assigned to a user.<br/><b>Saviynt equivalent:</b> Account Entitlements (The link between a specific User Account and an Entitlement object).`,
  LOOKUP_DEFINITIONS: `Static key-value pairs used to populate form drop-downs and translate data during reconciliation.<br/><b>Saviynt equivalent:</b> Datasets (Key-value lists used for form values and mapping logic).`,
};

export const OIM_TOOLTIP_DESCRIPTIONS = {
  applications: `OIM uses configured applications (IT Resources and Application Instances) to connect to target systems and manage provisioning.<br/><b>Saviynt equivalent:</b> Connection, Security System, Endpoint`,
  Account: `OIM accounts for all the connected and disconnected applications. This includes only provisioned and enabled accounts. You can migrate the account using data migration or you can reconcile them into Saviynt via Jobs.`,
  entitlements: `Represents count of access/permissions (entitlements) reconciled from target systems in OIM.`,
  Identity: `All OIM active and disabled users. You can migrate them from data migration tab. Please make sure the correct attribute mapping is saved before migration.`,
  datasets: `These are the lookups attached to application form's attribute as lookup filed or entitlement field.`,
  EmailTemplates: `Includes OOTB and custom deployed email templates. The migration will create respective email templates in Saviynt.`,
  RoleMemberships: `These are the user and role association for the roles in OIM which do not have any membership rule i.e direct assigned users.`,
};

export const STATUS = {
  STATUS_PENDING: 0,
  STATUS_IN_PROGRESS: 1,
  STATUS_COMPLETED: 2,
  STATUS_FAILED: 3,
  STATUS_SKIPPED: 4,
};

// Configuration Migration Constants
export const SKIP_SAV_CONFIG_TYPES = ['Datasets', 'UserGroups', 'Custom', 'EnterpriseRoles'];
export const BULK_MIGRATE_DATASET_TYPES = ['Datasets', 'Custom'];

// Visibility Constants - SailPoint
export const SAILPOINT_TILES_ORDER = [
  'Identity',
  'Accounts',
  'Entitlement',
  'Applications',
  'Correlation Config',
  'Password Policies',
  'Email Templates',
  'Workgroup',
  'Custom Object',
  'Bundle',
  'CertificationDefinition',
  'ObjectConfig',
  'Configuration',
  'UI Config',
  'YAML Config',
  'Policies',
  'Task Definition',
  'Workflow',
  'Rules',
  'IdentityTrigger',
  'Capability',
  'SPRights',
  'DynamicScope',
  'QuickLink',
  'Form',
  'Dictionary',
];

export const SAILPOINT_OBJECT_TYPE_TO_TILE_NAME = {
  Identity: 'Identity',
  Account: 'Accounts',
  Entitlement: 'Entitlement',
  Application: 'Applications',
  CorrelationConfig: 'Correlation Config',
  PasswordPolicy: 'Password Policies',
  EmailTemplate: 'Email Templates',
  Workgroup: 'Workgroup',
  Custom: 'Custom Object',
  Bundle: 'Bundle',
  CertificationDefinition: 'CertificationDefinition',
  ObjectConfig: 'ObjectConfig',
  Configuration: 'Configuration',
  UIConfig: 'UI Config',
  YAMLConfig: 'YAML Config',
  Policy: 'Policies',
  TaskDefinition: 'Task Definition',
  Workflow: 'Workflow',
  Rule: 'Rules',
  IdentityTrigger: 'IdentityTrigger',
  Capability: 'Capability',
  SPRight: 'SPRights',
  DynamicScope: 'DynamicScope',
  QuickLink: 'QuickLink',
  Form: 'Form',
  Dictionary: 'Dictionary',
};

export const SAILPOINT_EXCLUDED_TYPES = new Set(['DatabaseVersion', 'GroupDefinition']);

// Visibility Constants - OIM
export const OIM_TILES_ORDER = ['identities', 'accounts', 'entitlements', 'applications'];
